import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button, Dropdown, message, Modal } from 'antd';

import { Post as PostType } from '../../../../store/post';
import User from '../../User';
import { IconArrow, IconCommentMenu, IconReport } from '../../Icon';
import { useAuth } from '../../../../store/auth';
import { ModalReport } from '../../ModalReport';
import Carousel from '../../Carousel';
import Reactions from '../../Reactions';
import Replies from '../../Replies';
import { PaginatedResponse, PaginationProps } from '../../../../store/types';
import {
  CommentListParams,
  CommentOrderColumn,
} from '../../../../store/comment/list';
import { Comment } from '../../../../store/comment';
import { useCommentList } from '../../../../hooks/api/comments';
import { DefaultFetchError, getMessageInError } from '../../../../hooks/fetch';
import {
  usePostDelete,
  usePostReaction,
  usePostReactionDelete,
} from '../../../../hooks/api/posts';

import styles from './index.module.scss';
import useAuthModals from '../../../Layout/Menu/useAuthModals';
import { useActionGuard } from '../../../../hooks/useActionGuard';
import { ExclamationCircleOutlined } from '@ant-design/icons';

type SetPostProp =
  | ((value: PostType) => void)
  | Dispatch<SetStateAction<PostType | null>>;

interface PostParams {
  post: PostType;
  setPost?: SetPostProp;
  isHeader?: boolean;
  isPostSingle?: boolean;
  onDeleted?: (id: string) => void;
}

function Post({
  post,
  isHeader,
  isPostSingle,
  setPost,
  onDeleted,
}: PostParams) {
  const navigate = useNavigate();

  const { user, authorized } = useAuth();
  const { content, openLoginModal } = useAuthModals();

  const postDelete = usePostDelete(post.id);

  const isMyPost = post.user.id === user?.id;
  const isOwner = post.business?.ownerId === post.user.id;

  const [isOpenReportModal, setIsOpenReportModal] = useState(false);
  const [isOpenReplies, setIsOpenReplies] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const commentList = useCommentList();
  const [commentListResponse, setCommentListResponse] =
    useState<PaginatedResponse<Comment> | null>(null);

  const postReaction = usePostReaction();
  const postReactionDelete = usePostReactionDelete();

  const [listData, setListData] = useState<Comment[]>([]);

  // Auth gate
  const withAuth =
    <Args extends any[]>(fn: (...args: Args) => void | Promise<void>) =>
    (...args: Args) => {
      if (!authorized) {
        openLoginModal();
        return;
      }
      return fn(...args);
    };

  // Anti-spam
  const { guard, coolingDown, isBusy } = useActionGuard({
    cooldownMs: 700,
    dedupeWindowMs: 500,
    enqueueLatest: true,
  });

  const getComments = (params: CommentListParams) => {
    commentList
      .fetch(params)
      .then(setCommentListResponse)
      .catch((error: DefaultFetchError) => {
        message.error(getMessageInError(error));
      });
  };

  useEffect(() => {
    if (commentListResponse?.data) {
      if (commentListResponse.meta.page === 1) {
        setListData(commentListResponse.data || []);
      } else {
        setListData((prev) => {
          const newData = commentListResponse.data || [];
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueNew = newData.filter((item) => !existingIds.has(item.id));
          return [...prev, ...uniqueNew];
        });
      }
    }
  }, [commentListResponse]);

  const [pagination, setPagination] = useState<
    PaginationProps<CommentOrderColumn>
  >({
    page: 1,
    pageSize: 10,
    orderBy: 'ASC',
    orderByColumn: 'createdAt',
  });

  useEffect(() => {
    if (isOpenReplies) {
      getComments({
        postId: post.id,
        status: 'published',
        entity: 'post',
        ...pagination,
      });
    }
  }, [post, pagination, isOpenReplies]);

  // --- Reactions helpers ---
  const applyReaction = (params: { id: string; status: boolean }) => {
    if (!setPost) return;

    const currentlyLiked = post.reactions?.some((r) => r.status === true);
    const currentlyDisliked = post.reactions?.some((r) => r.status === false);

    const next: PostType =
      params.status === true
        ? {
            ...post,
            reactions: [{ id: params.id, status: true }],
            numberOfLikes: currentlyLiked
              ? post.numberOfLikes
              : post.numberOfLikes + 1,
            numberOfDislikes: currentlyDisliked
              ? Math.max(0, post.numberOfDislikes - 1)
              : post.numberOfDislikes,
          }
        : {
            ...post,
            reactions: [{ id: params.id, status: false }],
            numberOfLikes: currentlyLiked
              ? Math.max(0, post.numberOfLikes - 1)
              : post.numberOfLikes,
            numberOfDislikes: currentlyDisliked
              ? post.numberOfDislikes
              : post.numberOfDislikes + 1,
          };

    setPost(next);
  };

  const clearReaction = () => {
    if (!setPost) return;

    const currentlyLiked = post.reactions?.some((r) => r.status === true);
    const currentlyDisliked = post.reactions?.some((r) => r.status === false);

    const next: PostType = {
      ...post,
      reactions: [],
      numberOfLikes: currentlyLiked
        ? Math.max(0, post.numberOfLikes - 1)
        : post.numberOfLikes,
      numberOfDislikes: currentlyDisliked
        ? Math.max(0, post.numberOfDislikes - 1)
        : post.numberOfDislikes,
    };

    setPost(next);
  };

  // --- Server calls for reactions ---
  const sendLike = async () => {
    try {
      const data = await postReaction.fetch({ postId: post.id, status: true });
      if (data) applyReaction({ id: data.id, status: true });
    } catch (error) {
      message.error(getMessageInError(error as DefaultFetchError));
    }
  };

  const sendDislike = async () => {
    try {
      const data = await postReaction.fetch({ postId: post.id, status: false });
      if (data) applyReaction({ id: data.id, status: false });
    } catch (error) {
      message.error(getMessageInError(error as DefaultFetchError));
    }
  };

  const deleteReaction = async (id: string) => {
    try {
      await postReactionDelete.fetch(id);
      clearReaction();
    } catch (error) {
      message.error(getMessageInError(error as DefaultFetchError));
    }
  };

  // Public handlers used by <Reactions/>
  const like = withAuth(
    guard('post-like:' + post.id, async () => {
      const current = post.reactions?.[0];
      if (current?.status === false) {
        const del = await postReactionDelete.fetch(current.id);
        if (!del) return;
      }
      await sendLike();
    })
  );

  const dislike = withAuth(
    guard('post-dislike:' + post.id, async () => {
      const current = post.reactions?.[0];
      if (current?.status === true) {
        const del = await postReactionDelete.fetch(current.id);
        if (!del) return;
      }
      await sendDislike();
    })
  );

  const removeReaction = withAuth(
    guard('post-remove-reaction:' + post.id, async (id: string) => {
      await deleteReaction(id);
    })
  );

  // --- Delete flow ---
  const confirmDelete = (postId: string) => {
    if (!postId) return;

    Modal.confirm({
      title: 'Are you sure you want to delete this post?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      centered: true,
      getContainer: () => document.body,
      onOk: async () => {
        try {
          await postDelete.fetch();
          message.success('Post deleted');

          if (isPostSingle) {
            navigate('/share-your-two-cents');
            return;
          }

          setIsDeleted(true);
          onDeleted?.(post.id);
        } catch (e) {
          message.error(getMessageInError(e as DefaultFetchError));
        }
      },
    });
  };

  // --- Comments ---
  const onSubmitNewComment = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (setPost) {
      const next: PostType = {
        ...post,
        numberOfCommentsTotal: post.numberOfCommentsTotal + 1,
      };
      setPost(next);
    }
    setIsOpenReplies(true);
  };

  if (isDeleted) return null;

  return (
    <div>
      {isHeader ? (
        <div
          className={styles.postHeader}
          tabIndex={-1}
          role="banner"
          onClick={() =>
            navigate(
              isPostSingle
                ? `/business/${post.business?.id}`
                : `/share-your-two-cents/${post.id}`
            )
          }
        >
          <div className={styles.title}>
            <h3>{post.business?.name || ''}</h3>
            <span>{post.business?.address || ''}</span>
          </div>
          <div>
            <div className={styles.titleInfo}>
              <div className={styles.learnMore}>
                <NavLink
                  className={styles.link}
                  to={`/business/${post.business?.id}`}
                >
                  <IconArrow style={{ color: '#49cf6d' }} />
                  <span className={styles.link}>More info</span>
                </NavLink>
              </div>
              {post.category && post.subCategory ? (
                <div className={styles.category}>
                  {post.category.name || null} /{' '}
                  {post.subCategory.header || null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className={styles.wrapper}>
        <div className={styles.header}>
          <User user={post.user} date={post.createdAt} isOwner={isOwner} />
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                isMyPost
                  ? {
                      key: '1',
                      label: 'Edit',
                      onClick: () => navigate(`/post/${post.id}/update`),
                    }
                  : null,
                isMyPost
                  ? {
                      key: '3',
                      label: 'Delete',
                      danger: true,
                      onClick: () => confirmDelete(post.id),
                    }
                  : null,
                !isMyPost
                  ? {
                      key: '2',
                      label: 'Report',
                      onClick: () => setIsOpenReportModal(true),
                      icon: <IconReport />,
                    }
                  : null,
              ].filter(Boolean) as any[],
            }}
            trigger={['click']}
          >
            <Button size="small" type="text" style={{ padding: 0 }}>
              <IconCommentMenu />
            </Button>
          </Dropdown>
          {isOpenReportModal && (
            <ModalReport
              isOpen={isOpenReportModal}
              onCancel={() => setIsOpenReportModal(false)}
              createBody={{ type: 'post', postId: post.id }}
            />
          )}
        </div>

        {post.files.length > 0 ? (
          <div
            className={styles.carousel}
            style={{
              width: '100%',
              maxWidth: '799px',
              /** If post has image, then set min height to prevent flicker */
              minHeight: post.files?.[0]?.location ? '372px' : undefined,
            }}
          >
            <Carousel files={post.files} />
          </div>
        ) : null}

        <div className={styles.descriptionWrapper}>
          <div className={styles.descrioption}>{post.text}</div>
          <Reactions
            commentCreateParams={{
              body: { entity: 'post', postId: post.id },
            }}
            reactions={post.reactions || []}
            numberOfLikes={post.numberOfLikes}
            numberOfDislikes={post.numberOfDislikes}
            onLike={() => like()}
            onDislike={() => dislike()}
            removeReaction={(id) => removeReaction(id)}
            disabled={
              isBusy() ||
              coolingDown ||
              postReaction.loading ||
              postReactionDelete.loading
            }
            onSubmitNewComment={onSubmitNewComment}
            hideReply={isPostSingle}
          />
        </div>

        {!isPostSingle ? (
          <Replies
            ownerId={post.business?.ownerId}
            isOpen={isOpenReplies}
            onOpenChange={setIsOpenReplies}
            numberOfCommentsTotal={post.numberOfCommentsTotal}
            onRepplyDeleted={() => {
              if (!setPost) return;
              const next: PostType = {
                ...post,
                numberOfCommentsTotal: Math.max(
                  0,
                  post.numberOfCommentsTotal - 1
                ),
              };
              setPost(next);
            }}
            loading={listData.length === 0 && commentList.loading}
            onLoadMore={() =>
              setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
            }
            hasNextPage={!!commentListResponse?.meta?.hasNextPage}
            comments={listData}
            setComments={setListData}
          />
        ) : null}
      </div>

      {/* Mount auth modal content so openLoginModal() can render it */}
      {content}
    </div>
  );
}

export default Post;
